import { CarrierLogo } from "@/components/carriers/CarrierLogo";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { apiClient } from "@/lib/api/client";
import * as React from "react";
import { useNavigate } from "react-router-dom";

const NO_SELECTION = "__none__";

export default function ShipmentCancelPage() {
  const navigate = useNavigate();
  const [bookingId, setBookingId] = React.useState("");
  const [pickupNumber, setPickupNumber] = React.useState("");
  const [carrierCode, setCarrierCode] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [result, setResult] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [carrierOptions, setCarrierOptions] = React.useState<Array<{ label: string; value: string }>>([]);
  const [recentOptions, setRecentOptions] = React.useState<Array<{ id: string; pickup: string; carrier: string }>>([]);
  const hasIdentifier = bookingId.trim().length > 0 || pickupNumber.trim().length > 0;

  React.useEffect(() => {
    const loadData = async () => {
      try {
        const [carriers, shipments] = await Promise.all([
          apiClient.getCarriers(),
          apiClient.getShipments({ page: 1, pageSize: 50 }),
        ]);

        setCarrierOptions(
          carriers
            .map((carrier) => ({
              label: carrier.code ? `${carrier.name} (${carrier.code})` : carrier.name,
              value: carrier.code || carrier.name,
            }))
            .filter((item) => item.value.trim().length > 0),
        );

        setRecentOptions(
          shipments.items
            .filter((item) => item.trackingNumber)
            .map((item) => ({
              id: item.id,
              pickup: item.trackingNumber ?? "",
              carrier: item.carrier.code || item.carrier.name,
            })),
        );
      } catch {
        // Optional enhancement only.
      }
    };

    void loadData();
  }, []);

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!hasIdentifier) {
      setError("Enter at least a booking ID or a pickup/tracking number.");
      setResult(null);
      return;
    }

    setLoading(true);
    setResult(null);
    setError(null);

    try {
      const response = await apiClient.cancelBooking({
        bookingId: bookingId.trim() ? Number(bookingId) : undefined,
        pickupNumber: pickupNumber.trim() || undefined,
        carrierCode: carrierCode.trim() || undefined,
      });
      setResult(response.message ?? response.status ?? "Cancellation request submitted.");
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : "Unable to cancel booking.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader className="gap-3 md:flex md:flex-row md:items-start md:justify-between">
        <div>
          <CardTitle>Cancel Shipment</CardTitle>
          <CardDescription>Cancel by booking ID or pickup number + carrier.</CardDescription>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={() => navigate("/shipments")}>
          All Shipments
        </Button>
      </CardHeader>
      <CardContent>
        <form className="space-y-6" onSubmit={onSubmit}>
          {/* Shipment identification */}
          <div className="space-y-3">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground font-mono">Shipment Identification</p>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div className="space-y-1.5">
                <Label>Recent Shipment</Label>
                <Select
                  value={NO_SELECTION}
                  onValueChange={(value) => {
                    if (value === NO_SELECTION) return;
                    const selected = recentOptions.find((item) => item.id === value);
                    if (!selected) return;
                    setPickupNumber(selected.pickup);
                    setCarrierCode(selected.carrier);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select from recent data" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_SELECTION}>No selection</SelectItem>
                    {recentOptions.map((item) => (
                      <SelectItem key={item.id} value={item.id}>
                        <span className="flex items-center gap-2">
                          <CarrierLogo code={item.carrier} name={item.carrier} className="size-6 rounded" />
                          <span>#{item.id} · {item.pickup} · {item.carrier}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Booking ID</Label>
                <Input value={bookingId} onChange={(event) => setBookingId(event.target.value)} placeholder="12345" />
              </div>
              <div className="space-y-1.5">
                <Label>Pickup / Tracking #</Label>
                <Input
                  value={pickupNumber}
                  onChange={(event) => setPickupNumber(event.target.value)}
                  placeholder="PKUP-123"
                />
              </div>
            </div>
          </div>

          <div className="h-px bg-border" />

          {/* Carrier */}
          <div className="space-y-3">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground font-mono">Carrier</p>
            <div className="max-w-sm space-y-1.5">
              <Label>Carrier Code</Label>
              <Select value={carrierCode || NO_SELECTION} onValueChange={(value) => setCarrierCode(value === NO_SELECTION ? "" : value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select carrier" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_SELECTION}>No carrier</SelectItem>
                  {carrierOptions.map((carrier) => (
                    <SelectItem key={carrier.value} value={carrier.value}>
                      <span className="flex items-center gap-2">
                        <CarrierLogo code={carrier.value} name={carrier.label} className="size-6 rounded" />
                        <span>{carrier.label}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {error ? (
            <p role="alert" aria-live="polite" className="text-sm text-destructive">
              {error}
            </p>
          ) : null}
          {result ? (
            <p role="status" aria-live="polite" className="text-sm text-emerald-700">
              Success: {result}
            </p>
          ) : null}

          <Button type="submit" disabled={loading || !hasIdentifier}>
            {loading ? "Cancelling..." : "Cancel Shipment"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
